import React, { useState, useEffect } from 'react';
import '../fonts.css';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from "@mui/material";

const AiSection = () => {
  const [isVisible, setIsVisible] = useState(false);

  // 监测元素是否进入视口   
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setIsVisible(true);
      }
    }, { threshold: 0.2 });

    const section = document.getElementById('ai-section');
    if (section) observer.observe(section);

    return () => {
      if (section) observer.unobserve(section);
    };
  }, []);

  const isMobile = useMediaQuery("(max-width:1100px)");

  // 根据窗口宽度应用响应式样式
  const getResponsiveStyles = () => {

    return {
      sectionStyle: {
        padding: isMobile ? `var(--spacing-medium) 0` : `2rem 0 6rem`,
        backgroundColor: 'var(--color-background-light)',
        overflow: 'hidden',
      },

      // 移动设备使用垂直排列布局
      mobileContainerStyle: {
        display: isMobile ? 'flex' : 'none',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '1200px',
        margin: '0 auto',
        padding: `0 var(--spacing-medium)`,
        alignItems: 'center',
        gap: `var(--spacing-medium)`,
      },

      mobileImageStyle: {
        width: '100%',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        borderTopLeftRadius: '37.52px',
        borderTopRightRadius: '37.52px',
        borderBottomRightRadius: '37.52px',
        borderBottomLeftRadius: '0',
      },

      mobileContentStyle: {
        width: '100%',
        textAlign: 'left',
        padding: `var(--spacing-medium) 0`,
      },

      mobileTitleStyle: {
        fontSize: 'var(--font-size-xl)',
        fontWeight: 'bold',
        color: 'var(--color-text)',
        marginBottom: 'var(--spacing-small)',
      },

      mobileParagraphStyle: {
        fontSize: 'var(--font-size-base)',
        lineHeight: 1.6,
        color: 'var(--color-text-secondary)',
      },

      // 平板和桌面使用原有的叠加布局
      desktopContainerStyle: {
        display: isMobile ? 'none' : 'block',
        width: '100%',
        maxWidth: '1515px',
        margin: '0 auto',
        overflow: 'hidden',
        position: 'relative',
        // 特殊圆角设置：左上、右上和右下是圆角，左下是直角
        borderTopLeftRadius: '37.52px',
        borderTopRightRadius: '37.52px',
        borderBottomRightRadius: '37.52px',
        borderBottomLeftRadius: '0',
      },

      // 确保圆角正确设置
      imageContainerStyle: {
        width: '100%',
        minHeight: isMobile ? '200px' : '400px',
        position: 'relative',
        overflow: 'hidden',
        // 特殊圆角设置：左上、右上和右下是圆角，左下是直角
        borderTopLeftRadius: '37.52px',
        borderTopRightRadius: '37.52px',
        borderBottomRightRadius: '37.52px',
        borderBottomLeftRadius: '0',
      },

      imageStyle: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        objectPosition: 'center',
        // 确保图片本身也遵循容器的圆角
        borderTopLeftRadius: '37.52px',
        borderTopRightRadius: '37.52px',
        borderBottomRightRadius: '37.52px',
        borderBottomLeftRadius: '0',
      },

      contentOverlayStyle: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '40%',
        height: '100%',
        background: 'none',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center', // 将内容放置在底部
        alignItems: 'flex-start', // 靠左对齐
        padding: `var(--spacing-large) 5%`, // 保持适当的内边距
        // 确保叠加层也使用相同的圆角
        borderTopLeftRadius: '37.52px',
        borderTopRightRadius: '37.52px',
        borderBottomRightRadius: '37.52px',
        borderBottomLeftRadius: '0',
      },

      titleStyle: {
        fontSize: isMobile ? 'var(--font-size-xl)' : 'var(--font-size-xxl)',
        fontWeight: 'bold',
        color: 'white',
        marginBottom: '0',
        maxWidth: '600px',
        textShadow: 'var(--shadow-text)',
      },

      paragraphContainerStyle: {
        maxWidth: '550px',
      },

      paragraphStyle: {
        fontSize: isMobile ? 'var(--font-size-base)' : 'var(--font-size-lg)',
        fontWeight: '400',
        lineHeight: 1.2,
        color: 'white',
        marginBottom: '5px',
        textShadow: 'var(--shadow-subtitle)',
        letterSpacing: '0px',
      }
    };
  };

  const styles = getResponsiveStyles();
  const { t } = useTranslation();
  

  return (
    <section id="ai-section" className="full-width" style={styles.sectionStyle}>
      {/* 移动设备布局 */}
      <div className={`fade-in ${isVisible ? 'visible' : ''}`} style={styles.mobileContainerStyle}>
        <div style={styles.mobileImageStyle}>
          <img
            src="/atlas/cat1.png"
            alt="拍摄猫咪照片"
            style={{ width: '100%', display: 'block' }}
          />
        </div>
        <div style={styles.mobileContentStyle}>
          <h2 style={styles.mobileTitleStyle}>{t('AiTitle')}</h2>
          <p style={styles.mobileParagraphStyle}>
            {t('AiDescription')}
          </p>
        </div>
      </div>

      {/* 平板和桌面布局（特殊圆角样式） */}
      <div className={`fade-in ${isVisible ? 'visible' : ''}`} style={styles.desktopContainerStyle}>
        <div style={styles.imageContainerStyle}>
          <img
            src="/atlas/cat1.png"
            alt="貓眼睛特寫"
            style={styles.imageStyle}
          />
          <div style={styles.contentOverlayStyle}>
            <h2 style={styles.titleStyle} className={isVisible ? 'fade-in visible' : 'fade-in'}>{t('AiTitle')}</h2>
            <div style={styles.paragraphContainerStyle} className={isVisible ? 'fade-in visible' : 'fade-in'}>
              <p style={styles.paragraphStyle}>
                {t('AiDescription')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AiSection;
